import React, { useState } from 'react';
import './Admin.css';
import Login from './Login';
import { AdminProvider, useAdminContext } from './contexts/AdminProvider';
import { useAdminAuth } from './hooks/useAdminAuth';
import { useFlaggedPosts } from './hooks/useFlaggedPosts';
import { usePinnedPosts } from './hooks/usePinnedPosts';
import { useJobs } from './hooks/useJobs';
import { useBlog } from './hooks/useBlog';
import { AdminSidebar } from './components/AdminSidebar';
import { FlaggedPostsView } from './components/FlaggedPostsView';
import { PinnedPostsView } from './components/PinnedPostsView';
import { JobsView } from './components/JobsView';
import { BlogView } from './components/BlogView';
import { ConfirmationDialog } from './components/ConfirmationDialog';
import { ErrorAlert } from './components/ErrorAlert';
import type { View } from './types';

const VIEW_CONFIG: Record<View, { title: string; subtitle: string }> = {
  home: {
    title: 'Flagged Posts',
    subtitle: 'Review and moderate flagged content',
  },
  pinned: {
    title: 'Pinned Posts',
    subtitle: 'Manage posts pinned to the top of the popular feed',
  },
  blog: {
    title: 'Blog Posts',
    subtitle: 'Create and manage blog content for SEO',
  },
  jobs: {
    title: 'Background Jobs',
    subtitle: 'Monitor system job status and performance',
  },
};

function AdminDashboardContent() {
  const [currentView, setCurrentView] = useState<View>('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, handleLogin, handleLogout } = useAdminAuth();
  const { confirmationDialog, errorAlert, hideError } = useAdminContext();

  const {
    flaggedPosts,
    isLoading: flaggedPostsLoading,
    hidePost,
    banUser,
    unbanUser,
  } = useFlaggedPosts(!isAuthenticated);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    pinnedPosts,
    isSearching,
    isFetching,
    isPinning,
    isUnpinning,
    isReordering,
    pinPost,
    unpinPost,
    reorderPinnedPosts,
    hasMore,
    loadMore,
  } = usePinnedPosts(!isAuthenticated);

  const { jobs, isLoading: jobsLoading, isRefetching: jobsRefetching, restartJob } = useJobs(!isAuthenticated);

  const {
    posts: blogPosts,
    isLoading: blogLoading,
    isCreating: blogCreating,
    isUpdating: blogUpdating,
    isDeleting: blogDeleting,
    editingId: blogEditingId,
    setEditingId: setBlogEditingId,
    createPost: createBlogPost,
    updatePost: updateBlogPost,
    deletePost: deleteBlogPost,
  } = useBlog(!isAuthenticated);

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const { title, subtitle } = VIEW_CONFIG[currentView];

  return (
    <div className="admin-root">
      <div className="admin-layout">
        <AdminSidebar
          currentView={currentView}
          onViewChange={setCurrentView}
          onLogout={handleLogout}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="admin-main">
          <header className="admin-header">
            <button
              className="admin-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
            </button>
            <div>
              <h1 className="admin-header-title">{title}</h1>
              <p className="admin-header-subtitle">{subtitle}</p>
            </div>
          </header>
          <div className="admin-content">
            {currentView === 'home' && (
              <FlaggedPostsView
                flaggedPosts={flaggedPosts}
                isLoading={flaggedPostsLoading}
                onHidePost={hidePost}
                onBanUser={banUser}
                onUnbanUser={unbanUser}
              />
            )}
            {currentView === 'pinned' && (
              <PinnedPostsView
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                pinnedPosts={pinnedPosts}
                unpinnedPosts={searchResults}
                isSearching={isSearching}
                isFetching={isFetching}
                onPinPost={pinPost}
                onUnpinPost={unpinPost}
                onReorderPinnedPosts={reorderPinnedPosts}
                isPinning={isPinning}
                isUnpinning={isUnpinning}
                isReordering={isReordering}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            )}
            {currentView === 'blog' && (
              <BlogView
                posts={blogPosts}
                isLoading={blogLoading}
                isCreating={blogCreating}
                isUpdating={blogUpdating}
                isDeleting={blogDeleting}
                editingId={blogEditingId}
                onEdit={setBlogEditingId}
                onCreate={createBlogPost}
                onUpdate={updateBlogPost}
                onDelete={deleteBlogPost}
              />
            )}
            {currentView === 'jobs' && (
              <JobsView jobs={jobs} isLoading={jobsLoading} isRefetching={jobsRefetching} onRestartJob={restartJob} />
            )}
          </div>
        </main>
      </div>
      {confirmationDialog && (
        <ConfirmationDialog
          message={confirmationDialog.message}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
        />
      )}
      {errorAlert && (
        <ErrorAlert
          message={errorAlert.message}
          details={errorAlert.details}
          onClose={hideError}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AdminProvider>
      <AdminDashboardContent />
    </AdminProvider>
  );
}

export default App;
